import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import AreaChartIcon from "@mui/icons-material/Landscape";
import PieChartIcon from "@mui/icons-material/PieChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";

import useGetPatientHistogramData from "../../hooks/useGetPatientHistogramData";
import useGetPatientAiScoreHistory from "../../hooks/useGetPatientAiScoreHistory";
import useGetTreatmentOptions from "../../hooks/useGetTreatmentOptions";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CHART_COLORS = [
  "#EE295D",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
];

const CHART_TYPES = [
  { value: "bar", label: "Bar", Icon: BarChartIcon },
  { value: "line", label: "Line", Icon: ShowChartIcon },
  { value: "area", label: "Area", Icon: AreaChartIcon },
  { value: "pie", label: "Pie", Icon: PieChartIcon },
  { value: "table", label: "Table", Icon: TableChartIcon },
];

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;
const BASE_CHART_HEIGHT = 300;
const BASE_WIDTH_PER_POINT = 110;

const SCROLLBAR_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#c1c1c1 #f1f1f1",
  "&::-webkit-scrollbar": { height: 10 },
  "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1", borderRadius: 8 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#c1c1c1",
    borderRadius: 8,
    "&:hover": { backgroundColor: "#a8a8a8" },
  },
};

const SEVERITY_COLORS = {
  None: "#9E9E9E",
  Mild: "#F59E0B",
  Medium: "#F97316",
  Severe: "#EE295D",
};

const scoreAboveBarsPlugin = {
  id: "scoreAboveBars",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    ctx.save();
    ctx.font = "bold 13px sans-serif";
    ctx.fillStyle = "#333333";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    meta.data.forEach((bar, i) => {
      const value = chart.data.datasets[0].data[i];
      if (value !== null && value !== undefined) {
        ctx.fillText(String(value), bar.x, bar.y - 4);
      }
    });
    ctx.restore();
  },
};

const buildDataset = (question, color, chartType) => {
  const base = {
    label: question.title,
    borderColor: color,
    backgroundColor: `${color}33`,
    pointRadius: 5,
    pointHoverRadius: 7,
    spanGaps: true,
  };

  if (chartType === "pie") {
    return {
      label: question.title,
      data: question.data_points.map((dp) => dp.value),
      backgroundColor: CHART_COLORS.map((c) => `${c}CC`),
      borderColor: CHART_COLORS,
      borderWidth: 1,
    };
  }

  const data = question.data_points.map((dp) => dp.value);

  if (chartType === "area") return { ...base, data, fill: true, tension: 0.3 };
  if (chartType === "bar")
    return {
      ...base,
      data,
      borderWidth: 1,
      borderRadius: 6,
      backgroundColor: color,
    };

  return { ...base, data, tension: 0.3 };
};

const formatChoiceValue = (question, value) => {
  if (!question.choices || value === null || value === undefined) return value;
  const label = question.choices[value];
  return label !== undefined ? label : value;
};

const buildOptions = (question, chartType, labels, zoomLevel = 1) => {
  const values = question.data_points
    .map((dp) => dp.value)
    .filter((v) => v !== null && v !== undefined);

  const isPie = chartType === "pie";
  const isChoiceBased = Boolean(question.choices);
  const tickFontSize = Math.round(11 * zoomLevel);

  let yScale = { beginAtZero: false, ticks: { precision: 1, font: { size: tickFontSize } } };
  if (isChoiceBased) {
    const choiceValues = Object.keys(question.choices).map(Number);
    yScale = {
      min: Math.min(...choiceValues),
      max: Math.max(...choiceValues),
      ticks: { stepSize: 1, font: { size: tickFontSize } },
    };
  } else if (values.length > 0) {
    yScale.min = Math.floor((Math.min(...values) - 1) / 10) * 10;
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: isPie,
        position: "bottom",
        labels: { boxWidth: 12, font: { size: tickFontSize } },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.75)",
        padding: 10,
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (ctx) => {
            const y = isPie ? ctx.parsed : ctx.parsed.y;
            if (y === null || y === undefined) return "No data";
            return `${question.title}: ${formatChoiceValue(question, y)}`;
          },
        },
      },
    },
    ...(isPie
      ? {}
      : {
          scales: {
            x: {
              ticks: {
                maxRotation: 0,
                minRotation: 0,
                autoSkip: false,
                font: { size: Math.round(10 * zoomLevel) },
              },
              grid: { display: false },
            },
            y: {
              ...yScale,
              grid: { color: "rgba(0,0,0,0.06)" },
            },
          },
        }),
  };
};

const NoDataPlaceholder = ({ title }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 5,
      color: "text.disabled",
    }}
  >
    <BarChartIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
    <Typography variant="body2">
      No data recorded yet for <strong>{title}</strong>
    </Typography>
  </Box>
);

const QuestionDataTable = ({ question }) => (
  <TableContainer sx={{ maxHeight: 320 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Visit</TableCell>
          <TableCell align="right">Value</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {question.data_points.map((dp) => (
          <TableRow key={dp.visit_name}>
            <TableCell>{dp.visit_name}</TableCell>
            <TableCell align="right">
              {dp.value === null || dp.value === undefined
                ? "—"
                : formatChoiceValue(question, dp.value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const QuestionChart = ({ question, index }) => {
  const [chartType, setChartType] = useState("bar");
  const [zoomLevel, setZoomLevel] = useState(1);

  const color = CHART_COLORS[index % CHART_COLORS.length];
  const labels = question.data_points.map((dp) => dp.visit_name);
  const hasData = question.data_points.some((dp) => dp.value !== null);
  const isTable = chartType === "table";
  const isPie = chartType === "pie";

  const chartData = {
    labels,
    datasets: [buildDataset(question, color, chartType)],
  };

  const options = buildOptions(question, chartType, labels, zoomLevel);
  const ChartComponent = chartType === "bar" ? Bar : chartType === "pie" ? Pie : Line;

  const chartHeight = Math.round(BASE_CHART_HEIGHT * zoomLevel);
  const chartMinWidth = isPie
    ? "100%"
    : `max(100%, ${question.data_points.length * BASE_WIDTH_PER_POINT * zoomLevel}px)`;

  const handleZoomIn = () => setZoomLevel((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const handleZoomOut = () => setZoomLevel((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));

  return (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          backgroundColor: "grey.50",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          lineHeight={1.2}
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {question.title}
        </Typography>

        {!isTable && (
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={handleZoomOut}
              disabled={zoomLevel <= ZOOM_MIN}
              title="Zoom out"
            >
              <ZoomOutIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleZoomIn}
              disabled={zoomLevel >= ZOOM_MAX}
              title="Zoom in"
            >
              <ZoomInIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}

        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={(_, val) => val && setChartType(val)}
          size="small"
          sx={{ backgroundColor: "white", borderRadius: 2, flexShrink: 0 }}
        >
          {CHART_TYPES.map(({ value, label, Icon }) => (
            <ToggleButton
              key={value}
              value={value}
              title={label}
              sx={{
                px: 1,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 2,
          overflowX: isPie || isTable ? "visible" : "auto",
          ...(isPie || isTable ? {} : SCROLLBAR_SX),
        }}
      >
        {!hasData ? (
          <NoDataPlaceholder title={question.title} />
        ) : isTable ? (
          <QuestionDataTable question={question} />
        ) : (
          <Box sx={{ height: chartHeight, minWidth: chartMinWidth }}>
            <ChartComponent
              data={chartData}
              options={options}
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const SeverityLegend = ({ treatmentOptions }) => {
  if (!treatmentOptions?.length) return null;

  const sorted = [...treatmentOptions].sort((a, b) => a.score - b.score);

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
      {sorted.map((opt) => (
        <Box key={opt.score} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: SEVERITY_COLORS[opt.short_description] ?? "#9E9E9E",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {opt.short_description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ScoreTrendCard = ({ scores, mode }) => {
  if (!scores || scores.length < 2) return null;

  const first = mode === "repose" ? scores[0].repose_score : scores[0].active_score;
  const latest =
    mode === "repose"
      ? scores[scores.length - 1].repose_score
      : scores[scores.length - 1].active_score;

  if (first === latest) return null;

  const improving = latest < first;
  const modeLabel = mode === "repose" ? "At Rest" : "In Motion";
  const TrendIcon = improving ? TrendingDownIcon : TrendingUpIcon;

  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: 3,
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mt: 1.5,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: improving ? "#D1FAE5" : "#FEE2E2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <TrendIcon sx={{ fontSize: 20, color: improving ? "#059669" : "#DC2626" }} />
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          Score {improving ? "improving" : "worsening"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {modeLabel}: {first} → {latest}
        </Typography>
      </Box>
    </Paper>
  );
};

const AiScoreSection = ({ scores, treatmentOptions, scoreColorMap }) => {
  const [scoreMode, setScoreMode] = useState("repose");

  const chartData = useMemo(() => {
    const values = scores.map((s) => (scoreMode === "repose" ? s.repose_score : s.active_score));
    const labels = scores.map((s) => dayjs(s.completion_date).format("MMM D"));

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: values.map((v) => scoreColorMap[v] ?? "#9E9E9E"),
          borderRadius: 8,
          borderWidth: 0,
          barPercentage: 0.6,
        },
      ],
    };
  }, [scores, scoreMode, scoreColorMap]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(0,0,0,0.75)",
          padding: 10,
          callbacks: {
            label: (ctx) => `Score: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        x: {
          ticks: { maxRotation: 0, minRotation: 0, autoSkip: false, font: { size: 11 } },
          grid: { display: false },
        },
        y: {
          min: 0,
          max: 3,
          ticks: { stepSize: 1, font: { size: 11 } },
          grid: { color: "rgba(0,0,0,0.06)" },
        },
      },
    }),
    []
  );

  if (!scores.length) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.disabled" }}>
        <BarChartIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
        <Typography variant="body2">No completed sessions yet.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <ToggleButtonGroup
          value={scoreMode}
          exclusive
          onChange={(_, val) => val && setScoreMode(val)}
          size="small"
          sx={{
            backgroundColor: "#F3F4F6",
            borderRadius: 10,
            p: 0.5,
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              borderRadius: "20px !important",
              px: 3,
              py: 0.75,
              fontWeight: 600,
              fontSize: 13,
              textTransform: "none",
              color: "text.secondary",
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              },
            },
          }}
        >
          <ToggleButton value="repose">At Rest</ToggleButton>
          <ToggleButton value="active">In Motion</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <SeverityLegend treatmentOptions={treatmentOptions} />

      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ height: 280, minWidth: `max(100%, ${scores.length * 90}px)` }}>
          <Bar
            data={chartData}
            options={chartOptions}
            plugins={[scoreAboveBarsPlugin]}
            style={{ width: "100%", height: "100%" }}
          />
        </Box>
      </Box>

      <ScoreTrendCard scores={scores} mode={scoreMode} />
    </Box>
  );
};

const SurveyHistogramWebView = () => {
  const { trialId, patientId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [selectedFormIndex, setSelectedFormIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(null);

  const enabled = Boolean(trialId) && Boolean(patientId) && Boolean(token);

  const { data: forms, isLoading: formsLoading } = useGetPatientHistogramData({
    trialId,
    patientId,
    token,
    enabled,
  });

  const { data: aiScoreData, isLoading: aiScoreLoading } = useGetPatientAiScoreHistory({
    trialId,
    patientId,
    token,
    enabled,
  });

  const { data: treatmentOptions } = useGetTreatmentOptions({ token, enabled });

  useEffect(() => {
    if (aiScoreData !== undefined && activeTab === null) {
      setActiveTab(aiScoreData.is_session_based ? "ai" : "survey");
    }
  }, [aiScoreData, activeTab]);

  const scoreColorMap = useMemo(() => {
    if (!treatmentOptions?.length) return {};
    return Object.fromEntries(
      treatmentOptions.map((opt) => [
        opt.score,
        SEVERITY_COLORS[opt.short_description] ?? "#9E9E9E",
      ])
    );
  }, [treatmentOptions]);

  const isSessionBased = aiScoreData?.is_session_based === true;
  const isLoading = formsLoading || aiScoreLoading || activeTab === null;

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading your data…
        </Typography>
      </Box>
    );
  }

  const hasSurveyData = forms && forms.length > 0;

  if (!isSessionBased && !hasSurveyData) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 3,
        }}
      >
        <BarChartIcon sx={{ fontSize: 56, color: "text.disabled" }} />
        <Typography variant="h6" color="text.secondary">
          No charts configured
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center">
          No histogram data has been configured for this trial yet.
        </Typography>
      </Box>
    );
  }

  const selectedForm = forms?.[selectedFormIndex];

  return (
    <Box sx={{ backgroundColor: "#f7f8fa", minHeight: "100vh", pb: 6 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 3,
          py: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        {isSessionBased && (
          <Box
            sx={{ display: "flex", gap: 1, mb: hasSurveyData && activeTab === "survey" ? 2 : 0 }}
          >
            <Box
              component="button"
              onClick={() => setActiveTab("ai")}
              sx={{
                flex: 1,
                py: 1,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: activeTab === "ai" ? "primary.main" : "grey.300",
                backgroundColor: activeTab === "ai" ? "primary.main" : "white",
                color: activeTab === "ai" ? "white" : "text.secondary",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                transition: "all 0.15s",
              }}
            >
              <BarChartIcon sx={{ fontSize: 16 }} />
              AI Scores
            </Box>
            <Box
              component="button"
              onClick={() => setActiveTab("survey")}
              sx={{
                flex: 1,
                py: 1,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: activeTab === "survey" ? "primary.main" : "grey.300",
                backgroundColor: activeTab === "survey" ? "primary.main" : "white",
                color: activeTab === "survey" ? "white" : "text.secondary",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                transition: "all 0.15s",
              }}
            >
              <ShowChartIcon sx={{ fontSize: 16 }} />
              Survey Scores
            </Box>
          </Box>
        )}

        {activeTab === "survey" && hasSurveyData && (
          <FormControl fullWidth size="small">
            <InputLabel>Select Form</InputLabel>
            <Select
              value={selectedFormIndex}
              label="Select Form"
              onChange={(e) => setSelectedFormIndex(e.target.value)}
              sx={{ backgroundColor: "white", borderRadius: 2 }}
            >
              {forms.map((form, idx) => (
                <MenuItem key={`${form.form_type}_${form.form_id}`} value={idx}>
                  {form.form_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ maxWidth: 860, mx: "auto", px: 1.5, pt: 2 }}>
        {activeTab === "ai" && isSessionBased && (
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden", p: 2 }}>
            <AiScoreSection
              scores={aiScoreData.scores}
              treatmentOptions={treatmentOptions}
              scoreColorMap={scoreColorMap}
            />
          </Paper>
        )}

        {activeTab === "survey" && (
          <>
            {!hasSurveyData ? (
              <Paper elevation={1} sx={{ borderRadius: 3, p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No histogram data has been configured for this trial yet.
                </Typography>
              </Paper>
            ) : selectedForm?.questions.length === 0 ? (
              <Paper elevation={1} sx={{ borderRadius: 3, p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No questions configured for this form.
                </Typography>
              </Paper>
            ) : (
              selectedForm.questions.map((question, idx) => (
                <QuestionChart key={question.name} question={question} index={idx} />
              ))
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SurveyHistogramWebView;
